import React from 'react';
import { cn, useTheme } from '@/ui/theme/ThemeContext';
import { PRIORITY_ACTIVE_CLASSES, PRIORITY_LABELS } from '@/shared/utils/priorityMetadata';
import { useSettings } from '@/features/settings/contexts/SettingsContext';

interface ButtonOption<T extends string> {
  value: T;
  label: string;
  flex?: number;
  activeClass?: string;
}

interface ButtonGroupProps<T extends string> {
  label: string;
  value: T | undefined;
  options: ButtonOption<T>[];
  onChange: (value: T) => void;
  className?: string;
  disabled?: boolean;
}

export function ButtonGroup<T extends string>({
  label,
  value,
  options,
  onChange,
  className,
  disabled = false,
}: ButtonGroupProps<T>) {
  const { theme } = useTheme();

  return (
    <div className={cn("space-y-2", className)}>
      <label className={cn("text-sm font-medium", theme.textSecondary)}>{label}</label>
      <div className="flex gap-2">
        {options.map((option) => {
          const isActive = value === option.value;
          return (
            <button
              key={option.value}
              type="button"
              onClick={() => !disabled && onChange(option.value)}
              disabled={disabled}
              aria-pressed={isActive}
              data-state={isActive ? "active" : "inactive"}
              style={{ flex: option.flex || 1 }}
              className={cn(
                "px-3 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap",
                isActive
                  ? option.activeClass || theme.bgTertiary
                  : cn(theme.bgSecondary, theme.hover),
                "disabled:opacity-50 disabled:cursor-not-allowed"
              )}
            >
              {option.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// Pre-configured button groups for common use cases
export const StatusButtonGroup: React.FC<{
  value: string | undefined;
  onChange: (value: string) => void;
  className?: string;
  disabled?: boolean;
}> = ({ value, onChange, className, disabled }) => {
  const { theme } = useTheme();
  const { t } = useSettings();
  
  const options: ButtonOption<string>[] = [
    { value: 'todo', label: t('To Do'), flex: 0.8, activeClass: theme.bgTertiary },
    { value: 'in-progress', label: t('In Progress'), flex: 1.2, activeClass: theme.accentYellow },
    { value: 'done', label: t('Done'), flex: 0.8, activeClass: theme.accentGreen },
  ];

  return (
    <ButtonGroup
      label={t('Status')}
      value={value}
      options={options}
      onChange={onChange}
      className={className}
      disabled={disabled}
    />
  );
};

export const PriorityButtonGroup: React.FC<{
  value: string | undefined;
  onChange: (value: string) => void;
  className?: string;
  disabled?: boolean;
}> = ({ value, onChange, className, disabled }) => {
  const { t } = useSettings();
  const options: ButtonOption<string>[] = [
    { value: 'low', label: t(PRIORITY_LABELS.low), activeClass: PRIORITY_ACTIVE_CLASSES.low },
    { value: 'medium', label: t(PRIORITY_LABELS.medium), activeClass: PRIORITY_ACTIVE_CLASSES.medium },
    { value: 'high', label: t(PRIORITY_LABELS.high), activeClass: PRIORITY_ACTIVE_CLASSES.high },
    { value: 'critical', label: t(PRIORITY_LABELS.critical), activeClass: PRIORITY_ACTIVE_CLASSES.critical },
  ];

  return (
    <ButtonGroup
      label={t('Priority')}
      value={value}
      options={options}
      onChange={onChange}
      className={className}
      disabled={disabled}
    />
  );
};
